/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { connectHits, connectStateResults } from 'react-instantsearch-core';

import { compose } from 'recompose';

import { LectureItemLink } from 'bundles/clips/components/LectureItemLink';
import type { VLPItem } from 'bundles/clips/types/vlpSharedTypes';
import { ALGOLIA_ENTERPRISE_SHORT_FORM_CONTENT_INDEX } from 'bundles/enterprise-learner-search/constants';

type InjectedResults = {
  allSearchResults?: Record<string, $TSFixMe>;
};

type PropsFromCaller = {
  currentVideoItem: VLPItem;
  currentCourseId: string;
};

export type Props = InjectedResults & PropsFromCaller;

export type SearchResultItem = {
  atomId: string;
  objectUrl: string;
  name: string;
  durationMs: number;
  parentCourseId: string;
  parentCourseName: string;
};

const MAX_RELATED_VIDEOS_TO_DISPLAY = 15;

const containerStyles = css({ paddingLeft: 20, paddingRight: 24 });

export const RelatedVideosList = ({ allSearchResults, currentVideoItem, currentCourseId }: Props) => {
  const hits = (allSearchResults?.hits ||
    allSearchResults?.[ALGOLIA_ENTERPRISE_SHORT_FORM_CONTENT_INDEX]?.hits) as Array<SearchResultItem>;

  const getRelatedVideos = () => {
    const relatedVideosNotInCurrentCourse = hits.filter(
      (searchResultItem: SearchResultItem) => searchResultItem.parentCourseId !== currentCourseId
    );

    const groupedRelatedVideosByParentCourse = relatedVideosNotInCurrentCourse.reduce(
      (acc: { [key: string]: Array<SearchResultItem> }, searchResultItem: SearchResultItem) => {
        acc[searchResultItem.parentCourseName] = acc[searchResultItem.parentCourseName]
          ? [...acc[searchResultItem.parentCourseName], searchResultItem]
          : [searchResultItem];

        return acc;
      },
      {}
    );

    const numParentCourses = Object.keys(groupedRelatedVideosByParentCourse).length;
    let relatedVideosToShow: Array<SearchResultItem> = [];

    if (relatedVideosNotInCurrentCourse.length > MAX_RELATED_VIDEOS_TO_DISPLAY) {
      const numVideosPerCourse = Math.ceil(MAX_RELATED_VIDEOS_TO_DISPLAY / numParentCourses);

      Object.values(groupedRelatedVideosByParentCourse).forEach((courseVideos) => {
        for (let i = 0; i < Math.min(numVideosPerCourse, courseVideos.length); i += 1) {
          relatedVideosToShow.push(courseVideos[i]);
        }
      });
    } else {
      relatedVideosToShow = relatedVideosNotInCurrentCourse;
    }

    if (relatedVideosToShow.length > MAX_RELATED_VIDEOS_TO_DISPLAY) {
      relatedVideosToShow.length = MAX_RELATED_VIDEOS_TO_DISPLAY;
    }

    return relatedVideosToShow;
  };

  if (hits && hits.length > 0) {
    const relatedVideosToShow = getRelatedVideos();

    return (
      <div css={containerStyles} data-testid="related-videos-list">
        {relatedVideosToShow.map((searchResultItem) => (
          <LectureItemLink
            key={searchResultItem.atomId}
            url={searchResultItem.objectUrl}
            lectureName={searchResultItem.name}
            lectureTimeCommitment={searchResultItem.durationMs}
            isSelected={currentVideoItem.name === searchResultItem.name}
            trackingName="sfc_related_videos_link"
          />
        ))}
      </div>
    );
  }
  return null;
};

export default compose<Props, PropsFromCaller>(connectHits, connectStateResults)(RelatedVideosList);
